/**
 * NewReviewDialog — Phase 82
 *
 * Standalone "create a review" modal. Extracted from Reviews.jsx so it can
 * be triggered from anywhere (Approvals → New request → Send for review).
 * Auto-opens when the caller passes ?new=1 in the URL and strips the param
 * on close so page reloads don't reopen it.
 *
 * On submit: POST /api/reviews with { title, description, project_id,
 * reviewer_id, link }. Backend notifies the reviewer.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", description: "", project_id: "", reviewer_id: "", link: "" };

export default function NewReviewDialog({ triggerParam = "new" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open when ?new=1 is present (called from /approvals → Send for review).
  // Re-runs on every searchParams change so navigating within the same route
  // (e.g. /approvals → /approvals?new=1) still triggers the dialog.
  useEffect(() => {
    if (searchParams.get(triggerParam) === "1") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete(triggerParam);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, triggerParam, setSearchParams]);

  useEffect(() => {
    if (!open) return;
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    if (user?.role !== "client") {
      api.get("/users", { params: { kind: "internal" } }).then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, user?.role]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", form);
      toast.success("Review request sent — reviewer notified");
      setForm(EMPTY);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm" data-testid="new-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Send for review</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">Fill in the review details. The reviewer will get an email + in-app notification.</p>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Title *</Label>
            <Input
              className="rounded-sm mt-1"
              placeholder="e.g. Homepage v3 — final polish"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="new-review-title"
            />
          </div>
          <div>
            <Label className="label-overline">Description</Label>
            <Textarea
              rows={3}
              className="rounded-sm mt-1"
              placeholder="What needs review? Any context the reviewer should know?"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="new-review-description"
            />
          </div>
          <div>
            <Label className="label-overline">Project</Label>
            <Select
              value={form.project_id || "none"}
              onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}
            >
              <SelectTrigger className="rounded-sm mt-1" data-testid="new-review-project"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {user?.role !== "client" && (
            <div>
              <Label className="label-overline">Reviewer</Label>
              <Select
                value={form.reviewer_id || "none"}
                onValueChange={(v) => setForm({ ...form, reviewer_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-review-reviewer"><SelectValue placeholder="Anyone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Anyone</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-overline">Link (Figma, Drive…)</Label>
            <Input
              className="rounded-sm mt-1"
              placeholder="https://…"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              data-testid="new-review-link"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="new-review-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !form.title.trim()}
            className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white"
            data-testid="new-review-submit"
          >
            {busy ? "Sending…" : "Send for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
